/* eslint-disable */
import { useEffect, useState, useMemo } from "react";

import classNames from "classnames";

import { Alert } from "@/components/alert/alert";
import {
  EnvironmentalEffects
} from "@/components/environment/environmental-effects";
import { NightOverlay } from "@/components/environment/lighting-effects";
import { PokemonCampfireDisplay } from "@/components/pokemon/pokemon-campfire-display";
import { Button } from "@/components/ui/buttons/button";
import { AnimatedVFX } from "@/components/vfx/animated-vfx";
import { useDuelQueue } from "@/hooks/use-duel-queue";
import { useRaidStatus } from "@/hooks/use-raid-status";
import { BackIcon } from "@/icons";
import { useAuth } from "@/providers/auth-provider";
import { useRaid } from "@/providers/raid-provider";
import { useSPAContext } from "@/providers/spa-provider";
import { useUser } from "@/providers/user-provider";
import Battle from "@/routes/Battle";
import { Duel } from "@/routes/Duel";
import Raid from "@/routes/Raid";
import { listInventoryPokemons } from "@/services/inventory/list-inventory-pokemons";
import { RouteNames } from "@/types/route-names";
import { ArenaTeamSelectView } from "@/views/arena/arena-team-select-view";
import { PickPokemonView } from "@/views/battle/pick-pokemon-view";
import { BoxDetailsView } from "@/views/box/box-details-view";
import { BoxListView } from "@/views/box/box-list-view";
import { BreedView } from "@/views/breed/breed-view";
import { CollectionDetailsView } from "@/views/collection/collection-details-view";
import { CollectionListView } from "@/views/collection/collection-list-view";
import { DevRaidIntroView } from "@/views/dev/dev-raid-intro-view";
import { DojoTeamSelectView } from "@/views/dojo/dojo-team-select-view";
import { DojoTrainingConfigView } from "@/views/dojo/dojo-training-config-view";
import { DojoView } from "@/views/dojo/dojo-view";
import { DojoSandboxView } from "@/views/dojo/dojo-sandbox-view";
import { HomeBottomNav } from "@/views/home/home-bottom-nav";
import { HomeHeader } from "@/views/home/home-header";
import { IdeaBoardView } from "@/views/ideas/idea-board-view";
import { InventoryItemsView } from "@/views/inventory-items/inventory-items-view";
import { InventoryPokeView } from "@/views/inventory-poke/inventory-poke-view";
import { LeaderboardView } from "@/views/leaderboard/leaderboard-view";
import { PlayMenuView } from "@/views/play-menu/play-menu-view";
import { TeamEditView } from "@/views/poke-teams/team-edit-view";
import { TeamListView } from "@/views/poke-teams/team-list-view";
import { PokemonEggInfoView } from "@/views/pokemon-egg-info/pokemon-egg-info-view";
import { PokemonInfoView } from "@/views/pokemon-info/pokemon-info-view";
import { PlayerProfileView } from "@/views/profile/player-profile-view";
import { ProfileSettingsView } from "@/views/profile/profile-settings-view";
import { RaidCampaignSelectView } from "@/views/raid/raid-campaign-select";
import { RaidLobbyView } from "@/views/raid/raid-lobby-view";
import { RaidSelectView } from "@/views/raid/raid-select-view";
import { RaidStartView } from "@/views/raid/raid-start-view";
import { SendItemsView } from "@/views/send-items/send-items-view";
import { SendPokecoinsView } from "@/views/send-pokecoins/send-pokecoins-view";
import { SendPokeshardsView } from "@/views/send-pokeshards/send-pokeshards-view";
import { ShopView } from "@/views/shop/shop-view";

import CutsceneEditor from "./CutsceneEditor";

import type {
  WeatherType} from "@/components/environment/environmental-effects";


/**
 * Calculate night overlay opacity based on current local time
 * Full daylight (0 opacity): 10:00 - 16:00
 * Night (0.9 opacity): 22:00 - 04:00
 * Gradual transition during dawn/dusk
 */
const getNightOpacity = (): number => {
  const hour = new Date().getHours();

  // Full daylight: 10:00 - 16:00
  if (hour >= 10 && hour < 16) return 0;

  // Full night: 22:00 - 04:00
  if (hour >= 22 || hour < 4) return 0.9;

  // Dawn transition: 04:00 - 10:00 (0.9 -> 0)
  if (hour >= 4 && hour < 10) {
    return 0.9 * (1 - (hour - 4) / 6);
  }

  // Dusk transition: 16:00 - 22:00 (0 -> 0.9)
  return 0.9 * ((hour - 16) / 6);
};

const routeToView: Record<RouteNames, React.ReactNode> = {
  [RouteNames.PLAY_MENU]: null,
  [RouteNames.DOJO]: <DojoView />,
  [RouteNames.DOJO_TRAINING_CONFIG]: <DojoTrainingConfigView />,
  [RouteNames.DOJO_TEAM_SELECT]: <DojoTeamSelectView />,
  [RouteNames.DOJO_SANDBOX]: <DojoSandboxView />,
  // Dojo battle is rendered inside the Dojo flow views (not a standalone route view)
  [RouteNames.DOJO_BATTLE]: null,
  [RouteNames.INVENTORY_ITEMS]: null,
  [RouteNames.INVENTORY_POKE]: null,
  [RouteNames.LEADERBOARD]: null,
  [RouteNames.TEAM_EDIT]: null,
  [RouteNames.TEAM_LIST]: null,
  [RouteNames.SHOP]: <ShopView />,
  [RouteNames.BREED]: <BreedView />,
  [RouteNames.SEND_POKECOINS]: <SendPokecoinsView />,
  [RouteNames.SEND_POKESHARDS]: <SendPokeshardsView />,
  [RouteNames.SEND_ITEMS]: <SendItemsView />,
  [RouteNames.ARENA_TEAM_SELECT]: <ArenaTeamSelectView />,
  [RouteNames.DUEL_QUEUE]: <Duel />,
  [RouteNames.POKEMON_INFO]: <PokemonInfoView />,
  [RouteNames.EGG_INFO]: <PokemonEggInfoView />,
  [RouteNames.BATTLE_TEST]: <Battle />,
  [RouteNames.PLAYER_PROFILE]: <PlayerProfileView />,
  [RouteNames.PROFILE_SETTINGS]: <ProfileSettingsView />,
  // Raid routes
  [RouteNames.RAID_START]: <RaidStartView />,
  [RouteNames.RAID_SELECT]: <RaidSelectView />,
  [RouteNames.RAID_LOBBY]: <RaidLobbyView />,
  [RouteNames.RAID_ACTIVE]: <Raid />, // Unified route for all in-raid phases
  // Legacy campaign routes
  [RouteNames.RAID_CAMPAIGN_SELECT]: <RaidCampaignSelectView />,
  [RouteNames.RAID_STAGE_SELECT]: <RaidCampaignSelectView />, // TODO: Implement separate stage select view
  [RouteNames.DEV_RAID_INTRO]: <DevRaidIntroView />,
  [RouteNames.PICK_POKEMON]: <PickPokemonView />,
  // Ideas routes
  [RouteNames.IDEA_BOARD]: <IdeaBoardView />,
  // Box routes
  [RouteNames.BOX_LIST]: <BoxListView />,
  [RouteNames.BOX_DETAILS]: <BoxDetailsView />,
  // Collection routes
  [RouteNames.COLLECTION_LIST]: <CollectionListView />,
  [RouteNames.COLLECTION_DETAILS]: <CollectionDetailsView />,
};

export default function Home() {
  const {
    currentRoute,
    setCurrentRoute,
    setSelectedPokemons,
    setIntent,
    setViewedPlayerId,
    popBackAction,
  } = useSPAContext();
  const { getAccessToken } = useAuth();
  const {
    pokeCoins,
    pokeShards,
    playerCountry,
    username,
    mainTeamPokemons,
    duelStatus: initialDuelStatus,
    raidStatus: initialRaidStatus,
    state: userState,
  } = useUser();
  // Random weather on mount to show off the effects
  const [weather] = useState<WeatherType>("clouds");
  const nightOpacity = useMemo(() => getNightOpacity(), []);

  const loading = userState.status === "loading";

  // Use hooks to get duel and raid status for the PLAY button indicator
  const isOnHomeScreen = !currentRoute;
  const { queueStatus, currentDuelId } = useDuelQueue(
    isOnHomeScreen,
    initialDuelStatus,
  );
  const { currentRaidId, currentRaidStatus } = useRaidStatus(
    isOnHomeScreen,
    initialRaidStatus,
  );
  const { raidState, setRaidState } = useRaid();

  const isPlayMenuOpen = currentRoute === RouteNames.PLAY_MENU;
  const [renderPlayMenu, setRenderPlayMenu] = useState(false);

  const isInventoryPokeOpen = currentRoute === RouteNames.INVENTORY_POKE;
  const [renderInventoryPoke, setRenderInventoryPoke] = useState(false);

  const isInventoryItemsOpen = currentRoute === RouteNames.INVENTORY_ITEMS;
  const [renderInventoryItems, setRenderInventoryItems] = useState(false);

  const isLeaderboardOpen = currentRoute === RouteNames.LEADERBOARD;
  const [renderLeaderboard, setRenderLeaderboard] = useState(false);

  const isTeamListOpen = currentRoute === RouteNames.TEAM_LIST;
  const [renderTeamList, setRenderTeamList] = useState(false);

  const isTeamEditOpen = currentRoute === RouteNames.TEAM_EDIT;
  const [renderTeamEdit, setRenderTeamEdit] = useState(false);

  const isSendPokecoinsOpen = currentRoute === RouteNames.SEND_POKECOINS;
  const [renderSendPokecoins, setRenderSendPokecoins] = useState(false);

  const isSendPokeshardsOpen = currentRoute === RouteNames.SEND_POKESHARDS;
  const [renderSendPokeshards, setRenderSendPokeshards] = useState(false);

  useEffect(() => {
    if (!currentRoute) {
      setSelectedPokemons([]);
    }
  }, [currentRoute, setSelectedPokemons]);

  useEffect(() => {
    if (isPlayMenuOpen) {
      setRenderPlayMenu(true);
    } else {
      if (renderPlayMenu) {
        const timer = setTimeout(() => setRenderPlayMenu(false), 300);
        return () => clearTimeout(timer);
      }
    }
  }, [isPlayMenuOpen, renderPlayMenu]);

  useEffect(() => {
    if (isInventoryPokeOpen) {
      setRenderInventoryPoke(true);
    } else {
      if (renderInventoryPoke) {
        const timer = setTimeout(() => setRenderInventoryPoke(false), 500); // 500ms for slide animation
        return () => clearTimeout(timer);
      }
    }
  }, [isInventoryPokeOpen, renderInventoryPoke]);

  useEffect(() => {
    if (isInventoryItemsOpen) {
      setRenderInventoryItems(true);
    } else {
      if (renderInventoryItems) {
        const timer = setTimeout(() => setRenderInventoryItems(false), 500); // 500ms for slide animation
        return () => clearTimeout(timer);
      }
    }
  }, [isInventoryItemsOpen, renderInventoryItems]);

  useEffect(() => {
    if (isLeaderboardOpen) {
      setRenderLeaderboard(true);
    } else {
      if (renderLeaderboard) {
        const timer = setTimeout(() => setRenderLeaderboard(false), 500); // 500ms for slide animation
        return () => clearTimeout(timer);
      }
    }
  }, [isLeaderboardOpen, renderLeaderboard]);

  useEffect(() => {
    if (isTeamListOpen) {
      setRenderTeamList(true);
    } else {
      if (renderTeamList) {
        const timer = setTimeout(() => setRenderTeamList(false), 500); // 500ms for slide animation
        return () => clearTimeout(timer);
      }
    }
  }, [isTeamListOpen, renderTeamList]);

  useEffect(() => {
    if (isTeamEditOpen) {
      setRenderTeamEdit(true);
    } else {
      if (renderTeamEdit) {
        const timer = setTimeout(() => setRenderTeamEdit(false), 500); // 500ms for slide animation
        return () => clearTimeout(timer);
      }
    }
  }, [isTeamEditOpen, renderTeamEdit]);

  useEffect(() => {
    if (isSendPokecoinsOpen) {
      setRenderSendPokecoins(true);
    } else {
      if (renderSendPokecoins) {
        const timer = setTimeout(() => setRenderSendPokecoins(false), 500); // 500ms for slide animation
        return () => clearTimeout(timer);
      }
    }
  }, [isSendPokecoinsOpen, renderSendPokecoins]);

  useEffect(() => {
    if (isSendPokeshardsOpen) {
      setRenderSendPokeshards(true);
    } else {
      if (renderSendPokeshards) {
        const timer = setTimeout(() => setRenderSendPokeshards(false), 500); // 500ms for slide animation
        return () => clearTimeout(timer);
      }
    }
  }, [isSendPokeshardsOpen, renderSendPokeshards]);

  // Check for local test mode on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const localTest = urlParams.get("localTest") === "true";
    const devIntro = urlParams.get("devIntro") === "true";
    const testPickPokemon = urlParams.get("testPickPokemon") === "true";

    if (localTest) {
      console.log("[Home] Local test mode detected, navigating to BATTLE_TEST");
      setCurrentRoute(RouteNames.BATTLE_TEST);
    }

    if (devIntro) {
      console.log(
        "[Home] Dev intro mode detected, navigating to DEV_RAID_INTRO",
      );
      setCurrentRoute(RouteNames.DEV_RAID_INTRO);
    }

    if (testPickPokemon) {
      console.log(
        "[Home] Test Pick Pokemon mode detected, navigating to PICK_POKEMON",
      );
      // Mock the Telegram startapp parameter for testing
      if (!(window as any).Telegram) {
        (window as any).Telegram = {};
      }
      if (!(window as any).Telegram.WebApp) {
        (window as any).Telegram.WebApp = {};
      }
      if (!(window as any).Telegram.WebApp.initDataUnsafe) {
        (window as any).Telegram.WebApp.initDataUnsafe = {};
      }
      // Set a test startapp parameter: join_{battle_id}_{chat_id}
      (window as any).Telegram.WebApp.initDataUnsafe.start_param =
        "join_test123_123456789";
      setCurrentRoute(RouteNames.PICK_POKEMON);
    }

    // Parse startapp payload for deep linking
    const startParam = (window as any).Telegram?.WebApp?.initDataUnsafe
      ?.start_param;
    if (startParam && startParam.startsWith("join")) {
      console.log("[Home] startapp join detected:", startParam);
      setCurrentRoute(RouteNames.PICK_POKEMON);
    }
  }, [setCurrentRoute]);

  // Determine if there's an active game (duel or raid)
  const isDuelActive = queueStatus === "IN_DUEL" || queueStatus === "MATCHED";
  const isDuelWaiting = queueStatus === "WAITING";
  const hasActiveGame = isDuelActive || !!currentRaidId;

  /**
   * Handle play button click - go directly to the relevant view
   */
  const handlePlayClick = () => {
    // Check for alpha permission
    if (userState.status === "awaiting_alpha_approval") {
      Alert("You don't have alpha permission, request access from an admin.");
      return;
    }

    // If duel is active or waiting, go to duel queue (reconnect)
    if (isDuelActive || isDuelWaiting) {
      setCurrentRoute(RouteNames.DUEL_QUEUE);
      return;
    }

    // If raid is active, go to raid (reconnect)
    if (currentRaidId) {
      // If already connected to this raid and not in connecting state, just navigate
      if (
        raidState.raid_id === currentRaidId &&
        raidState.status !== "idle" &&
        raidState.status !== "connecting"
      ) {
        setCurrentRoute(RouteNames.RAID_ACTIVE);
        return;
      }

      // Otherwise, set connecting state to trigger connection
      setRaidState((prev) => ({
        ...prev,
        raid_id: currentRaidId,
        status: "connecting",
      }));
      setCurrentRoute(RouteNames.RAID_ACTIVE);
      return;
    }

    // No active game, go to play menu
    setCurrentRoute(RouteNames.PLAY_MENU);
  };

  const handleBack = () => {
    const handled = popBackAction();
    if (!handled) {
      setCurrentRoute(undefined);
    }
  };

  const handleCampfirePokemonClick = async (pokemon: any) => {
    const accessToken = getAccessToken();
    if (!accessToken) return;

    // Show loading state if we want, but let's just fetch
    try {
      const response = await listInventoryPokemons(accessToken);
      if (response.ok && response.data) {
        const fullPokemon = response.data.player_pokemons.find(
          (p) => p.id === pokemon.id,
        );
        if (fullPokemon) {
          setSelectedPokemons([fullPokemon]);
          setCurrentRoute(RouteNames.POKEMON_INFO);
        }
      }
    } catch (error) {
      console.error("Failed to fetch full pokemon data:", error);
      // Fallback to partial data if fetch fails
      setSelectedPokemons([pokemon]);
      setCurrentRoute(RouteNames.POKEMON_INFO);
    }
  };

  let currentView = currentRoute ? routeToView[currentRoute] : null;
  let cutsceneEditorView = false;

  // If /cutscene-editor is in the URL, show the cutscene editor
  if (window.location.pathname === "/cutscene-editor") {
    currentView = <CutsceneEditor />;
    cutsceneEditorView = true;
  }

  return (
    <main
      className={classNames(
        "w-full h-full bg-[#5CD599] relative z-1  overflow-hidden flex items-center justify-start flex-col",
        {
          "max-w-[480px] ": !cutsceneEditorView,
        },
      )}
      style={{
        backgroundImage: "url(/assets/app-ui/backgrounds/sky.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "bottom",
        backgroundRepeat: "no-repeat",
        animation: "scrollBackground 240s linear infinite",
        height: cutsceneEditorView
          ? "100svh"
          : "min(100svh, calc(480px * 18 / 9))",
      }}
      id="app-container"
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes scrollBackground {
            0% {
              background-position: 0% 50%;
            }
            100% {
              background-position: 100% 50%;
            }
          }
        `,
        }}
      />
      <div
        className="flex flex-col justify-start items-center w-full h-full"
        style={{
          backgroundImage: "url(/assets/app-ui/backgrounds/test.png)",
          backgroundSize: "105%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {currentView ? (
          currentView
        ) : (
          <>
            <EnvironmentalEffects weather={weather} />
            <NightOverlay opacity={nightOpacity} />
            {/* Top Bar */}
            <div
              className={classNames("w-full ", {
                "animate-slide-up-exit":
                  isInventoryPokeOpen ||
                  isInventoryItemsOpen ||
                  isLeaderboardOpen ||
                  isTeamListOpen ||
                  isTeamEditOpen ||
                  isPlayMenuOpen,
                "animate-slide-down":
                  (!isInventoryPokeOpen && renderInventoryPoke) ||
                  (!isInventoryItemsOpen && renderInventoryItems) ||
                  (!isLeaderboardOpen && renderLeaderboard) ||
                  (!isTeamListOpen && renderTeamList) ||
                  (!isTeamEditOpen && renderTeamEdit) ||
                  (!isPlayMenuOpen && renderPlayMenu),
              })}
            >
              <HomeHeader />
            </div>

            {/* Main Content Area */}
            <div className="relative flex flex-1 justify-center items-center p-4 w-full">
              {/* Merchant Button */}
              <div className="top-1/4 -left-2 z-30 absolute -translate-y-1/2">
                <Button
                  onClick={() => setCurrentRoute(RouteNames.SHOP)}
                  variant="orange"
                  className="flex justify-center items-center gap-0"
                >
                  <div className="flex flex-col justify-center items-center gap-1">
                    <img
                      src="/assets/icons/shop.png"
                      alt="Merchant"
                      className="w-8 h-8 object-contain"
                    />
                    <span className="drop-shadow-sm font-bold text-pk-gray-4 text-xs tracking-wide">
                      MERCHANT
                    </span>
                  </div>
                </Button>
              </div>

              {/* Center Content */}
              <div className="z-10 flex flex-col items-center gap-4 w-full">
                {loading ? (
                  <div className="flex justify-center items-center w-48 h-48">
                    <div className="border-4 border-pk-blue-4 border-t-transparent rounded-full w-12 h-12 animate-spin" />
                  </div>
                ) : (
                  <>
                    {/* AnimatedVFX Examples */}
                    <div className="top-32 right-16 z-50 absolute">
                      <AnimatedVFX
                        id="healing-1-green"
                        spriteUrl="/assets/spells/self/healing-1-green.png"
                        spriteScale={0.3}
                        spriteAlpha={1}
                        position={{ x: 0, y: 0 }}
                        loops={1}
                        speed={1.5}
                        ping_pong={false}
                      />
                    </div>

                    {/* Pokemon Team Display */}
                    <PokemonCampfireDisplay
                      pokemons={mainTeamPokemons}
                      onPokemonClick={handleCampfirePokemonClick}
                      className="w-full translate-y-[120px] xs:translate-y-[160px]"
                    />
                  </>
                )}
              </div>
            </div>

            {/* Play Button */}
            <div className="z-40 flex justify-end pb-1 w-full">
              <Button
                onClick={handlePlayClick}
                variant={
                  isDuelWaiting
                    ? "yellow"
                    : currentRaidId && currentRaidStatus === "lobby"
                    ? "garnet"
                    : hasActiveGame
                    ? "red"
                    : "gray"
                }
              >
                <div className="flex items-center gap-3 px-12 py-2">
                  <span className="drop-shadow-lg text-2xl xs:text-3xl filter">
                    {hasActiveGame ? "🔌" : ""}
                  </span>
                  <div className="flex flex-col items-start">
                    <span className="drop-shadow-[2px_2px_0_rgba(0,0,0,0.5)] font-black text-white text-xl xs:text-2xl tracking-wider">
                      {isDuelWaiting
                        ? "IN QUEUE..."
                        : hasActiveGame
                        ? "RECONNECT"
                        : "PLAY"}
                    </span>
                    <span
                      className={`text-xs xs:text-sm font-bold tracking-[0.2em] uppercase drop-shadow-sm -mt-1 ${
                        isDuelWaiting
                          ? "text-yellow-400 animate-pulse"
                          : isDuelActive
                          ? "text-red-300 animate-pulse"
                          : currentRaidId && currentRaidStatus === "lobby"
                          ? "text-pk-yellow-1"
                          : currentRaidId
                          ? "text-red-300 animate-pulse"
                          : "text-pk-yellow-1"
                      }`}
                    >
                      {isDuelWaiting
                        ? "Waiting..."
                        : isDuelActive
                        ? "DUEL ACTIVE"
                        : currentRaidId
                        ? currentRaidStatus === "lobby"
                          ? "IN LOBBY"
                          : "RAID ACTIVE"
                        : ""}
                    </span>
                  </div>
                </div>
              </Button>
            </div>

            {/* Bottom Navigation */}
            <HomeBottomNav />

            {renderPlayMenu && (
              <div
                className={classNames("absolute inset-0 z-50", {
                  "animate-slide-down": isPlayMenuOpen,
                  "animate-slide-up-exit": !isPlayMenuOpen,
                })}
              >
                <PlayMenuView />
              </div>
            )}

            {renderInventoryPoke && (
              <div
                className={classNames("absolute inset-0 z-50", {
                  "animate-slide-down": isInventoryPokeOpen,
                  "animate-slide-up-exit": !isInventoryPokeOpen,
                })}
              >
                <InventoryPokeView />
              </div>
            )}

            {renderInventoryItems && (
              <div
                className={classNames("absolute inset-0 z-50", {
                  "animate-slide-down": isInventoryItemsOpen,
                  "animate-slide-up-exit": !isInventoryItemsOpen,
                })}
              >
                <InventoryItemsView />
              </div>
            )}

            {renderLeaderboard && (
              <div
                className={classNames("absolute inset-0 z-50", {
                  "animate-slide-down": isLeaderboardOpen,
                  "animate-slide-up-exit": !isLeaderboardOpen,
                })}
              >
                <LeaderboardView />
              </div>
            )}

            {renderTeamList && (
              <div
                className={classNames("absolute inset-0 z-50", {
                  "animate-slide-down": isTeamListOpen,
                  "animate-slide-up-exit": !isTeamListOpen,
                })}
              >
                <TeamListView />
              </div>
            )}

            {renderTeamEdit && (
              <div
                className={classNames("absolute inset-0 z-50", {
                  "animate-slide-down": isTeamEditOpen,
                  "animate-slide-up-exit": !isTeamEditOpen,
                })}
              >
                <TeamEditView />
              </div>
            )}

            {renderSendPokecoins && (
              <div
                className={classNames("absolute inset-0 z-50", {
                  "animate-slide-down": isSendPokecoinsOpen,
                  "animate-slide-up-exit": !isSendPokecoinsOpen,
                })}
              >
                <SendPokecoinsView />
              </div>
            )}

            {renderSendPokeshards && (
              <div
                className={classNames("absolute inset-0 z-50", {
                  "animate-slide-down": isSendPokeshardsOpen,
                  "animate-slide-up-exit": !isSendPokeshardsOpen,
                })}
              >
                <SendPokeshardsView />
              </div>
            )}
          </>
        )}

        {/* Footer with Share and Back buttons (only shown when in a view) */}
        {!cutsceneEditorView &&
          (currentView ||
            renderPlayMenu ||
            renderInventoryPoke ||
            renderInventoryItems ||
            renderLeaderboard ||
            renderTeamList ||
            renderTeamEdit ||
            renderSendPokecoins ||
            renderSendPokeshards) &&
          currentRoute !== RouteNames.DUEL_QUEUE &&
          currentRoute !== RouteNames.RAID_ACTIVE && (
            <footer className="right-0 bottom-0 left-0 z-50 absolute flex justify-start items-center w-full pointer-events-none">
              <div className="translate-x-2 translate-y-2 pointer-events-auto">
                <Button variant="default" onClick={handleBack}>
                  <BackIcon width={48} height={48} />
                </Button>
              </div>
            </footer>
          )}
      </div>
    </main>
  );
}
